import { useRef, memo } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Typography, TextField } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import { CopyBlockDto, CopyType } from '@act/shared/data-transfer-objects';
import { useFormContext, Controller } from 'react-hook-form';

interface DraggableCopyBlockProps {
  copyType: CopyType;
  block: CopyBlockDto;
  index: number;
  entryIndex: number;
  canDelete: boolean;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onChange: (
    blockIndex: number,
    updatedBlock: { title?: string; copy: string }
  ) => void;
  onDelete: (blockIndex: number) => void;
}

interface DragItem {
  index: number;
  entryIndex: number;
  id: string;
  type: string;
}

const DraggableCopyBlock = memo(
  ({
    copyType,
    block,
    index,
    entryIndex,
    canDelete,
    onMove,
    onChange,
    onDelete,
  }: DraggableCopyBlockProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const { control } = useFormContext();

    const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: string | symbol | null }
    >({
      accept: `copy-block-${entryIndex}`,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item: DragItem, monitor) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) {
          return;
        }

        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        onMove(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: `copy-block-${entryIndex}`,
      item: () => ({
        index,
        entryIndex,
        id: `copy-block-${entryIndex}-${index}`,
        type: 'copy-block',
      }),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const textFieldSx = {
      '& .MuiInputBase-input': {
        color: 'text.primary',
      },
      '& .MuiInputLabel-root': {
        color: 'text.primary',
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.23)',
        },
        '&:hover fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'primary.main',
        },
      },
    };

    drag(drop(ref));

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          opacity: isDragging ? 0.4 : 1,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
          p: 2,
        }}
        data-handler-id={handlerId}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
          }}
        >
          <DragIndicatorIcon sx={{ color: 'text.secondary', cursor: 'move' }} />
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Block {index + 1}
          </Typography>
          {canDelete && (
            <Box
              component="span"
              onClick={() => onDelete(index)}
              sx={{
                marginLeft: 'auto',
                display: 'flex',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '50%',
                opacity: '0.25',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'error.lighter',
                },
              }}
            >
              <RemoveCircleOutlinedIcon fontSize="small" color="error" />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {copyType === 'ordered-list' && (
            <Controller
              name={`copyBlocks.${index}.title`}
              control={control}
              defaultValue={block.title || ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    onChange(index, { ...block, title: e.target.value });
                  }}
                  label="Block Title (Optional)"
                  fullWidth
                  sx={textFieldSx}
                />
              )}
            />
          )}
          <Controller
            name={`copyBlocks.${index}.copy`}
            control={control}
            defaultValue={block.copy}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={e => {
                  field.onChange(e);
                  onChange(index, { ...block, copy: e.target.value });
                }}
                label="Copy"
                multiline
                rows={3}
                fullWidth
                sx={textFieldSx}
              />
            )}
          />
        </Box>
      </Box>
    );
  }
);

export default DraggableCopyBlock;
